import { Component, OnDestroy, OnInit, ViewChild, ViewChildren } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { marker as m } from '@colsen1991/ngx-translate-extract-marker';
import { ActionBarGroup } from '@common/components/action-bar/action-bar.interface';
import { AppControlComponent } from '@common/components/app-control/app-control.component';
import { CanDeactivateReturn, DeactivableComponent } from '@common/guards/can-deactivate.guard';
import { User } from '@common/models/user';
import { ValueOf } from '@common/models/util.interface';
import { ViewMode } from '@common/models/view-mode';
import { CommonService } from '@common/services/common.service';
import { QueryService } from '@common/services/query.service';
import { ToastrNotificationService } from '@common/services/toastr-notification.service';
import { tapSuccessResult } from '@ngneat/query';
import { TranslateService } from '@ngx-translate/core';
import { SelectEvent, TabStripComponent } from '@progress/kendo-angular-layout';
import { DialogService } from '@services/dialog.service';
import _ from 'lodash';
import { Subject, takeUntil } from 'rxjs';

@Component({ template: '' })
export abstract class BaseViewComponent<TQuery = any> implements OnInit, OnDestroy, DeactivableComponent {
    destroy$ = new Subject<boolean>();

    user: User;

    readonly viewMode: boolean;
    readonly createMode: boolean;
    readonly editMode: boolean;
    actionBar: ActionBarGroup[];
    public selection = [];
    public hasUnsavedChanges = false;

    protected queryService: QueryService;
    protected translateService: TranslateService;
    protected toastrNotificationService: ToastrNotificationService;
    protected activatedRoute: ActivatedRoute;
    protected dialogService: DialogService;
    protected router: Router;

    public mode: ValueOf<typeof ViewMode>;
    protected id: number | string;
    model: any = {};
    query$: any;
    tabIndex: number;
    editPermission: string;
    createPermission: string;

    abstract entityName: string;
    arrayProperties: string[] = []; // Initialize empty array for array properties in create mode
    parentRoute: string;
    title: string;
    headerTitle = m('Details');
    activeTab = 0;
    initialModel = {};
    /**
     * Extra parameters to be passed alongside the ID of the entity
     */
    loadExtraParams = {};
    /**
     * @deprecated
     * Here until pages are refactored to use new NavigationService. This will be removed in the future.
     */
    breadcrumbs = null;
    /**
     * @deprecated
     * Here until pages are refactored to use new NavigationService. This will be removed in the future.
     */
    currentNavItem = null;

    @ViewChildren(AppControlComponent) appControls: AppControlComponent[];
    @ViewChild(TabStripComponent) tabstrip: TabStripComponent;

    getTitle() {
        const entityTitle = this.translateService.instant(this.entityName.replace(/([A-Z])/g, ' $1').trim());
        return this.createMode ? `${this.translateService.instant('New')} ${entityTitle}` : `${this.getIdentifier()}`;
    }

    protected constructor(protected common: CommonService) {
        this.queryService = common.queryService;
        this.translateService = common.translateService;
        this.toastrNotificationService = common.toastrNotificationService;
        this.activatedRoute = common.activatedRoute;
        this.dialogService = common.dialogService;
        this.router = common.router;

        this.id = common.activatedRoute.snapshot.params.id;
        this.mode = common.activatedRoute.snapshot.data.mode;
        this.createMode = this.mode === ViewMode.create;
        this.viewMode = this.mode === ViewMode.view;
        this.editMode = !this.viewMode;
        this.user = this.common.userService.currentUserSubject.getValue();

        this.parentRoute = window.location.pathname
            .split('/')
            .slice(0, this.createMode ? -1 : -2)
            .join('/');

        this.activeTab = +this.router.routerState.snapshot.root.queryParams.tab || 0;
        this.actionBar = [
            {
                label: 'Edit Actions',
                items: [
                    {
                        label: 'Save',
                        icon: 'faSolidFloppyDisk',
                        variant: 'success',
                        isVisible: () => this.canEdit() && !this.viewMode,
                        onClick: () => this.saveChanges()
                    },
                    {
                        label: 'Edit',
                        variant: 'primary',
                        icon: 'faSolidPenToSquare',
                        isVisible: () => this.canEdit() && this.viewMode,
                        isDisabled: () => this.isEditDisabled(),
                        onClick: () => this.navigateToEditMode()
                    },
                    {
                        label: 'Cancel',
                        variant: 'danger',
                        icon: 'faSolidBan',
                        isVisible: () => this.editMode,
                        onClick: () => this.cancelChanges()
                    },
                    {
                        label: 'Back',
                        variant: 'secondary',
                        icon: 'faSolidArrowLeftLong',
                        isVisible: () => this.viewMode,
                        onClick: () => this.navigateToPreviousRoute()
                    }
                ]
            }
        ];
    }

    canDeactivate(): CanDeactivateReturn {
        if (!this.hasUnsavedChanges) return true;
        const deactivateSubject = new Subject<boolean>();
        this.dialogService
            .confirm({
                options: {
                    title: this.translateService.instant('Unsaved changes'),
                    message: this.translateService.instant(
                        'There are some unsaved changes. Do you want to leave and discard them?'
                    )
                }
            })
            .then((value) => {
                if (value) {
                    deactivateSubject.next(true);
                }
                deactivateSubject.next(false);
            });
        return deactivateSubject;
    }

    ngOnInit() {
        this.initialize();
    }

    async initialize() {
        if (this.createMode) await this.createEntity();
        else this.loadModel();
    }

    createEntity(): void | Promise<void> {
        if (!this.canCreateNew()) {
            this.navigateToList();
            this.toastrNotificationService.show({ type: 'error', message: 'User does not have create permissions' });
            return;
        }
        this.model = this.initialModel;
        this.arrayProperties.forEach((prop) => (this.model[prop] = []));
    }

    loadModel() {
        this.query$ = this.queryService.getQuery<TQuery>(
            this.entityName,
            { id: this.id, ...this.loadExtraParams },
            { injector: this.common.injector, refetchOnWindowFocus: this.viewMode }
        ).result$;
        this.query$
            .pipe(
                takeUntil(this.destroy$),
                tapSuccessResult<any>((data) => {
                    const mappedModel = this.mapData(data);

                    if (mappedModel == null) {
                        this.toastrNotificationService.show({
                            type: 'error',
                            message: this.translateService.instant('Record not found')
                        });
                        this.model = {};
                        void this.navigateToList();
                        return;
                    }

                    this.model = mappedModel;
                    this.modelLoaded();
                })
            )
            .subscribe();
    }

    getIdentifier() {
        return this.model.id;
    }

    mapData(data) {
        return data;
    }

    modelLoaded(): any {
        if (!this.model) return this.navigateToList();
        this.actionBar = [...this.actionBar]; // this triggers change detection in actionbar so that buttons can update (isHidden, isVisible, etc.)
        this.expandModelProperties();
        if (this.mode === ViewMode.edit && !this.canEdit()) {
            this.navigateToViewMode();
            this.toastrNotificationService.show({ type: 'error', message: 'User does not have edit permissions' });
            return;
        }
        this.title = this.getTitle();
        this.tabNavigate();
        if (this.editMode) this.hasUnsavedChanges = true;
    }

    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
    }

    canCancel() {
        return !this.viewMode;
    }

    cancelChanges() {
        this.hasUnsavedChanges = false;
        if (this.createMode) this.navigateToList();
        else this.navigateToViewMode();
    }

    canEdit() {
        if (this.editPermission) return this.user.hasPermission(this.editPermission);
        return true;
    }

    isEditDisabled() {
        return false;
    }

    hasPermission(permission) {
        return this.user.hasPermission(permission);
    }

    canCreateNew() {
        if (this.createPermission) return this.user?.hasPermission(this.createPermission);
        return true;
    }

    canClone() {
        return this.viewMode;
    }

    saveCommand(customData = {}): Promise<any> {
        return this.queryService.getCommandMutation().mutateAsync({
            command: `Save${this.entityName}`,
            data: { saveModel: this.model, action: this.mode, ...customData },
            invalidate: this.invalidateKey(this.entityName)
        });
    }

    /**
     * Overridable function for creating a custom key for invalidating cache
     */
    invalidateKey(entityName: string) {
        return `${entityName}s`;
    }

    beforeSave() {}

    async saveChanges(customData?: any) {
        if (!this.canSave()) return;

        this.beforeSave();
        const result = await this.saveCommand(customData);
        if (result?.error) return;
        await this.afterSave(result);
    }

    async afterSave(model) {
        this.hasUnsavedChanges = false;
        await this.navigateToViewMode(model);
        this.toastrNotificationService.show({ type: 'success', message: 'Save successful' });
    }

    beforeClone() {}

    async cloneEntity() {
        this.beforeClone();
        this.model.id = -1;
        const result = await this.saveCommand({ action: ViewMode.create });
        if (result?.error) return;
        await this.afterClone(result);
    }

    async afterClone(model) {
        await this.navigateToEditMode(model);
        this.toastrNotificationService.show({ type: 'success', message: 'Clone successful' });
    }

    async executeCommand(
        commandName: string,
        data = {},
        customCallback: (data?: any) => any | Promise<void> = null
    ): Promise<any> {
        return this.queryService
            .getCommandMutation()
            .mutateAsync({ command: commandName, data: { ...data, id: this.model.id }, invalidate: this.entityName })
            .then(async (data) => {
                this.toastrNotificationService.show({ type: 'success', message: 'Success' });
                if (!customCallback) this.initialize();
                else await customCallback(data);
                return data;
            });
    }

    navigateToViewMode(model = this.model) {
        return this.router.navigate([`${this.parentRoute}/view/${model.id}`], this.getNavigationExtras());
    }

    navigateToEditMode(model = this.model) {
        return this.router.navigate([`${this.parentRoute}/edit/${model.id}`], this.getNavigationExtras());
    }

    navigateToList(): Promise<boolean> {
        return this.router.navigate([`${this.parentRoute}/list/`]);
    }

    navigateToPreviousRoute() {
        window.history.back();
    }

    expandModelProperties(model = this.model) {
        Object.getOwnPropertyNames(model).forEach((prop) => {
            // Set id properties for app-control binding
            if (_.isArray(model[prop]))
                model[prop].forEach((item) => {
                    if (!_.isString(item)) {
                        this.expandModelProperties(item);
                        item[`${_.camelCase(this.entityName)}Id`] = this.model.id;
                    }
                });
            else if (_.isObject(model[prop])) {
                model[`${prop}Id`] = model[prop]?.['id'];
                this.expandModelProperties(model[prop]);
            }
        });
    }

    getNavigationExtras(): NavigationExtras {
        return { queryParamsHandling: 'merge', onSameUrlNavigation: 'reload', replaceUrl: true };
    }

    // If we are using kendo-tabstip
    onTabSelect(select: SelectEvent) {
        this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: { tab: +select.index },
            ...this.getNavigationExtras()
        });
        this.activeTab = +select.index;
    }

    canSave(appControls = this.appControls): boolean {
        if (appControls.some((x) => !!x.error)) {
            this.toastrNotificationService.show({ type: 'error', message: 'Some required fields are empty' });
            return false;
        }

        return true;
    }

    tabNavigate() {
        setTimeout(() => {
            this.tabstrip?.selectTab(this.activeTab);
            this.onTabSelect({ index: this.activeTab } as SelectEvent);
        });
    }
}
